import { useEffect, useState } from "react";
import { BusyLabel } from "@/components/loading";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Save, User as UserIcon, AlertTriangle, IdCard, MapPin, Phone } from "lucide-react";

const ID_TYPES = [
  { value: "cedula", label: "Cédula (10 dígitos)", digits: 10, numeric: true },
  { value: "ruc", label: "RUC (13 dígitos)", digits: 13, numeric: true },
  { value: "pasaporte", label: "Pasaporte", numeric: false },
];

/** Numeric-only sanitizer for cédula / RUC / phone / bank account. */
export const digitsOnly = (v, max) => {
  const d = (v || "").replace(/\D+/g, "");
  return typeof max === "number" ? d.slice(0, max) : d;
};

export { ID_TYPES };

/** Shared editable profile card — used by Buyer, Seller and Admin dashboards
 * and by /verify. Non-sensitive fields save immediately; changing id_type or
 * id_number resets the account to `pendiente_revision` so admin re-checks. */
export function EditProfileCard() {
  const { user, refresh } = useAuth();
  const [form, setForm] = useState({
    name: "", phone: "", province: "", city: "",
    id_type: "cedula", id_number: "",
  });
  const [provinces, setProvinces] = useState([]);
  const [cities, setCities] = useState([]);
  const [loadingCities, setLoadingCities] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => { api.get("/geo/provinces").then(r => setProvinces(r.data)).catch(() => {}); }, []);

  useEffect(() => {
    if (!user) return;
    setForm({
      name: user.name || "",
      phone: user.phone || "",
      province: user.province || "",
      city: user.city || "",
      id_type: user.id_type || (user.ruc ? "ruc" : "cedula"),
      id_number: user.id_number || user.cedula || user.ruc || "",
    });
  }, [user]);

  useEffect(() => {
    if (!form.province) { setCities([]); return; }
    setLoadingCities(true);
    api.get("/geo/cities", { params: { province: form.province } })
      .then((r) => setCities(r.data))
      .catch(() => setCities([]))
      .finally(() => setLoadingCities(false));
  }, [form.province]);

  const idCfg = ID_TYPES.find(t => t.value === form.id_type) || ID_TYPES[0];
  const initialIdType = user?.id_type || (user?.ruc ? "ruc" : "cedula");
  const initialIdNumber = user?.id_number || user?.cedula || user?.ruc || "";
  const identityChanged = form.id_type !== initialIdType || form.id_number !== initialIdNumber;

  const upd = (k, v) => setForm(f => k === "province" ? { ...f, province: v, city: "" } : { ...f, [k]: v });

  const save = async () => {
    if (!form.name || form.name.trim().length < 2) return toast.error("Ingresa tu nombre completo");
    if (!form.phone || form.phone.length < 7) return toast.error("Teléfono inválido");
    if (!form.province || !form.city) return toast.error("Selecciona provincia y ciudad");
    if (idCfg.numeric && form.id_number.length !== idCfg.digits) return toast.error(`${idCfg.label} debe tener ${idCfg.digits} dígitos`);
    if (!idCfg.numeric && form.id_number.trim().length < 5) return toast.error("Pasaporte inválido");
    setSaving(true);
    try {
      const r = await api.put("/users/me/profile", {
        name: form.name,
        phone: form.phone,
        province: form.province,
        city: form.city,
        id_type: form.id_type,
        id_number: form.id_number,
      });
      if (r.data?.requires_reverification) {
        toast.success("Datos guardados. Cambiaste tu identificación → tu cuenta vuelve a verificación.");
      } else {
        toast.success("Datos actualizados");
      }
      await refresh();
    } catch (err) {
      toast.error(err.response?.data?.detail || "No se pudo actualizar");
    } finally { setSaving(false); }
  };

  if (!user) return null;

  return (
    <div className="bg-[#111] border border-[#262626] rounded-md p-6 space-y-5" data-testid="edit-profile-card">
      <div className="flex items-center gap-2">
        <UserIcon size={18} className="text-[#EF4444]" />
        <h3 className="font-display text-xl">Editar mi perfil</h3>
      </div>
      <p className="text-xs text-neutral-500 -mt-2">Nombre, teléfono, provincia y ciudad se guardan al instante. Cambiar la identificación te devuelve a verificación pendiente para que el admin la revise.</p>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <Label className="text-neutral-400 mb-1.5 block text-xs uppercase tracking-wider"><UserIcon size={11} className="inline mr-1" />Nombre completo</Label>
          <Input data-testid="edit-profile-name" value={form.name} onChange={(e) => upd("name", e.target.value)} className="bg-[#171717] border-[#262626] text-white" />
        </div>
        <div>
          <Label className="text-neutral-400 mb-1.5 block text-xs uppercase tracking-wider"><Phone size={11} className="inline mr-1" />Teléfono (solo dígitos)</Label>
          <Input
            data-testid="edit-profile-phone"
            inputMode="numeric"
            pattern="\d*"
            value={form.phone}
            onChange={(e) => upd("phone", digitsOnly(e.target.value, 15))}
            className="bg-[#171717] border-[#262626] text-white"
          />
        </div>
        <div>
          <Label className="text-neutral-400 mb-1.5 block text-xs uppercase tracking-wider"><MapPin size={11} className="inline mr-1" />Provincia</Label>
          {/* Native <select> — Radix Select causes iOS Safari black-overlay bug */}
          <select
            data-testid="edit-profile-province"
            value={form.province}
            onChange={(e) => upd("province", e.target.value)}
            className="w-full bg-[#171717] border border-[#262626] text-white rounded-md h-10 px-3"
          >
            <option value="">Selecciona provincia</option>
            {provinces.map(p => <option key={p} value={p}>{p}</option>)}
          </select>
        </div>
        <div>
          <Label className="text-neutral-400 mb-1.5 block text-xs uppercase tracking-wider">Ciudad</Label>
          <select
            data-testid="edit-profile-city"
            value={form.city}
            onChange={(e) => upd("city", e.target.value)}
            disabled={!form.province || loadingCities}
            className="w-full bg-[#171717] border border-[#262626] text-white rounded-md h-10 px-3 disabled:opacity-50"
          >
            <option value="">
              {!form.province ? "Elige provincia primero" : loadingCities ? "Cargando…" : `Elige ciudad (${cities.length})`}
            </option>
            {cities.map(c => <option key={c} value={c}>{c}</option>)}
          </select>
        </div>
      </div>

      {/* Identity section — sensitive */}
      <div className="pt-5 border-t border-[#262626]">
        <div className="flex items-center gap-2 mb-3">
          <IdCard size={16} className="text-amber-400" />
          <h4 className="font-semibold text-sm">Identificación</h4>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <Label className="text-neutral-400 mb-1.5 block text-xs uppercase tracking-wider">Tipo</Label>
            <Select value={form.id_type} onValueChange={(v) => upd("id_type", v)}>
              <SelectTrigger data-testid="edit-profile-id-type" className="bg-[#171717] border-[#262626] text-white"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-[#171717] border-[#262626] text-white">
                {ID_TYPES.map(t => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-neutral-400 mb-1.5 block text-xs uppercase tracking-wider">Número</Label>
            <Input
              data-testid="edit-profile-id-number"
              inputMode={idCfg.numeric ? "numeric" : "text"}
              value={form.id_number}
              onChange={(e) => {
                const v = idCfg.numeric ? digitsOnly(e.target.value, idCfg.digits) : e.target.value.toUpperCase();
                upd("id_number", v);
              }}
              placeholder={idCfg.numeric ? `${idCfg.digits} dígitos` : "Ej: X1234567"}
              className="bg-[#171717] border-[#262626] text-white font-mono"
            />
          </div>
        </div>
        {identityChanged && (
          <div className="mt-3 p-3 rounded-md bg-amber-500/10 border border-amber-500/40 text-xs text-amber-200 flex gap-2" data-testid="edit-profile-identity-warning">
            <AlertTriangle size={14} className="shrink-0 mt-0.5" />
            <span>Al cambiar tu identificación, tu cuenta vuelve a <b>Pendiente de verificación</b> y debes subir tus documentos nuevos. Podrás publicar y comprar tras aprobación del admin.</span>
          </div>
        )}
      </div>

      <Button data-testid="edit-profile-save" onClick={save} disabled={saving} className="bg-[#EF4444] hover:bg-[#DC2626] w-full sm:w-auto">
        <Save size={14} className="mr-2" /><BusyLabel busy={saving} text="Guardando…">Guardar cambios</BusyLabel>
      </Button>
    </div>
  );
}

export default EditProfileCard;
